<template>
    <entrance-page-layout>
        <form @submit.prevent="requestResetPassword(email, captchaResponse, $event)" class="container" novalidate>
            <div class="reset-pass-row">
                <span class="password-img">
                    <img src="@/assets/gifs/password.gif"
                         alt="password"
                         width="80"
                         height="85"
                    >
                </span>
                <span>
                    <router-link class="bodyStrong back-link" to="/login">
                        <IconBase class="back-icon"
                                  width="12"
                                  height="8"
                                  display="inline-flex"
                                  icon-name="backIcon"></IconBase>
                        Back
                    </router-link>
                </span>
            </div>
            <span class="titleMedium">Reset password</span>
            <span class="body">
                Enter the email address you use to log in, and we'll send you a link to reset your password.
            </span>
            <div class="inputs-container" :class="{ 'no-captcha': !isCaptchaDisplayed }">
                <form-input v-model="email"
                            id="email"
                            placeholder="Email"
                            inputType="email"
                            :is-disabled="loading"
                            :maxLength="64"
                            icon="v2/user-circle"
                            icon-position="left"
                            :showValidationErrors="showValidationErrors"
                            :pattern="usernameRegex"
                            :patternError="EMAIL_FORMAT_ERROR"
                            @changedValue="inputValueChange($event, 'email')"
                            :customValidations="[{
                                validationFunc: () => !wrongEmail,
                                errorMsg: emailDoesntExistError
                            }]"
                            :triggerCustomValidationOnChange="email"
                            :isRequired="true"></form-input>
            <div class="captcha" v-if="isCaptchaDisplayed">
                <Captcha @captchaCallback="onCallbackCaptchaResponse"
                         @captchaExpiredCallback="onCallbackExpiredCaptcha"
                         captchaId="requestToResetPasswordCaptcha">
                </Captcha>
                <div class="error errorMessage bodyStrong"
                     v-if="captchaRequiredError">Approve you are not a robot</div>
            </div>
            </div>
            <div class="reset-pass-row footer">
                <Button id="reset-password-button" type="submit" :loading="loading">Reset password</Button>
            </div>
        </form>
    </entrance-page-layout>
</template>

<script>
import FormInput from 'components/inputs/formInput/FormInput.vue';
import links from 'services/metaData/links';
import { usernameRegex } from 'services/utils/regex';
import auth from 'services/auth';
import IconBase from 'components/IconBase.vue';
import analytics from 'services/utils/analytics';
import captchaService from 'services/captcha';
import Captcha from '@/pages/entrance/signUp/components/captcha.vue';
import EntrancePageLayout from '@/pages/entrance/components/EntrancePageLayout.vue';
import { EMAIL_FORMAT_ERROR } from 'services/metaData/errors';
import Button from 'components/button/Button.vue';

export default {
    name: 'RequestResetPasswordPage',
    components: {
        Button,
        IconBase,
        EntrancePageLayout,
        Captcha,
        FormInput
    },
    data() {
        return {
            links,
            email: '',
            wrongEmail: false,
            showValidationErrors: false,
            emailValid: false,
            loading: false,
            usernameRegex,
            emailDoesntExistError:
                'The email does not exist. Make sure you use the same capitalization as you did when logging in.',
            isCaptchaDisplayed: false,
            captchaResponse: null,
            EMAIL_FORMAT_ERROR,
            captchaRequiredError: false,
            invalidTokenError: 'Token invalid or expired.'
        };
    },
    async created() {
        this.isCaptchaDisplayed = await captchaService.checkDisplayCaptcha();

        if (this.$route.query.invalidToken) {
            this.$notification.open({ body: this.invalidTokenError, type: 'error' });
        }
    },
    methods: {
        onCallbackCaptchaResponse(captchaResponse) {
            analytics.track('AccountResetPassword', 'captchaSuccess');
            this.captchaResponse = captchaResponse;
            this.captchaRequiredError = false;
        },
        onCallbackExpiredCaptcha() {
            analytics.track('AccountResetPassword', 'captchaExpired');
            this.captchaResponse = null;
        },
        inputValueChange({ isValid, value }, key) {
            if (key === 'email') {
                this.emailValid = isValid;

                if (value === this.email) return;
                this.wrongEmail = false;
                this.showValidationErrors = false;
            }
        },
        async requestResetPassword(email, captchaResponse, event) {
            if (event.submitter) {
                event.submitter.focus();
            }

            if (!this.emailValid) {
                this.showValidationErrors = true;
                return;
            }

            if (this.isCaptchaDisplayed && !this.captchaResponse) {
                this.captchaRequiredError = true;
                return;
            }

            if (this.isCaptchaDisplayed) {
                analytics.track('AccountResetPassword', 'captchaDisplayed');
                if (window.mixpanel && window.mixpanel.people !== undefined) {
                    window.mixpanel.people.set({ $captchaResetPassword: true });
                }
            }

            this.loading = true;

            try {
                await auth.requestResetPassword(email, captchaResponse);
                await this.$router.push({ path: '/forgot/sent', query: { email } });
            } catch (error) {
                this.wrongEmail = true;
            }

            this.loading = false;
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide.V2.scss";
@import "~styles/buttons.V2.scss";

.container {
    display: flex;
    position: relative;
    flex-direction: column;
    padding: $spacing-xxl-6;
}

.messageWrap.bodyStrong.error-message.type-error {
    margin: $spacing-xl 0 0;
}

.reset-pass-row {
    display: flex;
    width: 100%;
    justify-content: space-between;
}

.footer {
    align-items: center;
    justify-content: flex-end;
}

.titleMedium {
    line-height: normal;
    color: $dark-500;
    margin-top: $spacing-xl;
}

.body {
    color: $dark-300;
    margin-top: $spacing-xs;
}

.inputs-container {
    margin-top: $spacing-xl;

    &.no-captcha {
        margin-bottom: $spacing-xxl-6;
    }
}

.captcha {
    margin-top: $spacing-xl;
    margin-bottom: $spacing-xl;
}

.back-link {
    color: $primary-500;
    text-decoration: none;
    display: flex;
    align-items: center;
}

.back-icon {
    width: 16px;
    height: 16px;
    margin-right: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.password {
    margin-top: $spacing-m;
}

::v-deep #reset-password-button {
    width: 151px;
}

.password-img {
    transform: translateX(-10px);
}
</style>
